// components/building/AmenityList.tsx
/**
 * @mockup /design/mockups/01-mobile-building-landing.png
 * @description Building amenities display with icons
 * Design System: Warm Sophistication - subtle backgrounds, warm neutrals
 */
import {
  Waves,
  Dumbbell,
  Shirt,
  Car,
  UserCheck,
  Wifi,
  Dog,
  Bike,
  TreePine,
  Shield,
  type LucideIcon,
} from 'lucide-react';

// Map amenity names to icons
const amenityIcons: Record<string, LucideIcon> = {
  'pool': Waves,
  'outdoor pool': Waves,
  'indoor pool': Waves,
  'gym': Dumbbell,
  'fitness centre': Dumbbell,
  'fitness center': Dumbbell,
  'laundry': Shirt,
  'laundry room': Shirt,
  'in-suite laundry': Shirt,
  'parking': Car,
  'underground parking': Car,
  'concierge': UserCheck,
  '24/7 concierge': UserCheck,
  'wifi': Wifi,
  'internet': Wifi,
  'pet friendly': Dog,
  'pets allowed': Dog,
  'bike storage': Bike,
  'bicycle storage': Bike,
  'garden': TreePine,
  'rooftop': TreePine,
  'security': Shield,
  '24/7 security': Shield,
};

function getAmenityIcon(amenity: string): LucideIcon {
  const key = amenity.toLowerCase();
  return amenityIcons[key] || UserCheck; // Default icon
}

function formatAmenityLabel(amenity: string): string {
  // Simplify display labels
  const simplifications: Record<string, string> = {
    'outdoor pool': 'Pool',
    'indoor pool': 'Pool',
    'fitness centre': 'Gym',
    'fitness center': 'Gym',
    'laundry room': 'Laundry',
    'underground parking': 'Parking',
    '24/7 concierge': 'Concierge',
    '24/7 security': 'Security',
    'bicycle storage': 'Bike Storage',
  };
  return simplifications[amenity.toLowerCase()] || amenity;
}

interface AmenityListProps {
  amenities: string[];
}

export function AmenityList({ amenities }: AmenityListProps) {
  if (!amenities || amenities.length === 0) {
    return null;
  }

  return (
    <section className="pt-2">
      <h2 className="font-display text-display-sm text-text mb-4">Amenities</h2>

      <div className="flex justify-between items-start overflow-x-auto pb-2 no-scrollbar gap-2">
        {amenities.slice(0, 5).map((amenity, index) => {
          const Icon = getAmenityIcon(amenity);
          return (
            <div
              key={index}
              className="flex flex-col items-center min-w-[64px] text-center"
            >
              <div className="w-12 h-12 rounded-lg bg-bg-subtle flex items-center justify-center mb-2 border border-border">
                <Icon size={24} className="text-text-secondary" strokeWidth={1.5} />
              </div>
              <span className="text-label-sm text-text-muted font-medium">
                {formatAmenityLabel(amenity)}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}
